import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserRoleEnum } from '../enums/user.enum';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  // BehaviorSubject holds the current permission list
  private permissions$ = new BehaviorSubject<string[]>([]);

  constructor() {
    this.refreshFromLocalStorage();
  }

  refreshFromLocalStorage() {
    const user = localStorage.getItem('user');
    const parsedUser = user ? JSON.parse(user) : null;

    // Handle both structures: parsedUser.permissions (new) and parsedUser.data.permissions (old)
    const raw = parsedUser?.permissions || parsedUser?.data?.permissions || [];

    if (!raw || (Array.isArray(raw) && raw.length === 0)) {
      this.permissions$.next([]);
      return;
    }
    if (Array.isArray(raw)) {
      const permissions = raw.map((p) => String(p));
      this.permissions$.next(permissions);
      return;
    }
    this.permissions$.next([String(raw)]);
    return;
  }

  getPermissions$(): Observable<string[]> {
    return this.permissions$.asObservable();
  }

  getCurrentPermissions(): string[] {
    return this.permissions$.getValue();
  }

  hasAny(required: string[] = [], current: string[] = this.getCurrentPermissions()): boolean {
    return required.some((r) => current.includes(r));
  }

  hasAll(required: string[] = [], current: string[] = this.getCurrentPermissions()): boolean {
    return required.every((r) => current.includes(r));
  }

  hasNone(required: string[] = [], current: string[] = this.getCurrentPermissions()): boolean {
    return !required.some((r) => current.includes(r));
  }

  hasPermission(permission: string | string[]): boolean {
    const permsToCheck = Array.isArray(permission) ? permission : [permission];
    return this.hasAny(permsToCheck);
  }

  private getCurrentUser(): any {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;
      const user = JSON.parse(userStr);
      return user?.data || user || null;
    } catch {
      return null;
    }
  }

  getCurrentRoles(): string[] {
    const user = this.getCurrentUser();
    return user?.roles || [];
  }

  hasRole(roles: UserRoleEnum | UserRoleEnum[] | string | string[]): boolean {
    const currentRoles = this.getCurrentRoles();
    if (!currentRoles.length) return false;

    const rolesToCheck = Array.isArray(roles) ? roles : [roles];
    return rolesToCheck.some((role) => currentRoles.includes(role));
  }

  hasAllRoles(roles: UserRoleEnum[] | string[]): boolean {
    const currentRoles = this.getCurrentRoles();
    if (!currentRoles.length) return false;

    return roles.every((role) => currentRoles.includes(role));
  }

  isAdmin(): boolean {
    return this.hasRole([UserRoleEnum.SUPER_ADMIN, UserRoleEnum.ADMIN]);
  }

  isSuperAdmin(): boolean {
    return this.hasRole(UserRoleEnum.SUPER_ADMIN);
  }

  isClientRegistry(): boolean {
    return this.hasRole(UserRoleEnum.CLIENT_REGISTRY);
  }

  isRegistrar(): boolean {
    return this.hasRole(UserRoleEnum.REGISTRAR);
  }

  isRegistry(): boolean {
    return this.hasRole(UserRoleEnum.REGISTRY);
  }

  canReviewApplications(): boolean {
    return this.hasRole([
      UserRoleEnum.SUPER_ADMIN,
      UserRoleEnum.ADMIN,
      UserRoleEnum.REGISTRAR,
      UserRoleEnum.REGISTRY,
    ]);
  }
}
